import { useState } from 'react';
import { X, Clock, Plus } from 'lucide-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  useRepository,
} from '@city-sage/aurum-avis-labs-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { QueryKeys } from '../../lib/QueryKeys';
import { useIsAuthenticated } from '@azure/msal-react';

interface ChatHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectThread: (threadId: string) => void;
}

interface GroupedConversations {
  today: any[];
  thisWeek: any[];
  thisMonth: any[];
  older: any[];
}

// Date grouping utility
function groupItemsByDate(items: any[], getDate: (item: any) => string): GroupedConversations {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const grouped: GroupedConversations = {
    today: [],
    thisWeek: [],
    thisMonth: [],
    older: [],
  };

  items.forEach(item => {
    const itemDate = new Date(getDate(item));
    if (itemDate >= today) {
      grouped.today.push(item);
    } else if (itemDate >= weekAgo) {
      grouped.thisWeek.push(item);
    } else if (itemDate >= monthAgo) {
      grouped.thisMonth.push(item);
    } else {
      grouped.older.push(item);
    }
  });

  return grouped;
}

// Simple date formatting - Using i18n aware formatting
function formatDate(date: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC' // Use UTC to avoid timezone mismatches
  }).format(new Date(date));
}

function formatRelativeTime(date: string): string {
  const now = new Date();
  const itemDate = new Date(date);
  const diffInMinutes = Math.floor((now.getTime() - itemDate.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInMinutes < 1440) {
    return `${Math.floor(diffInMinutes / 60)}h ago`;
  } else {
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  }
}

export function ChatHistoryModal({
  isOpen,
  onClose,
  onSelectThread,
}: ChatHistoryModalProps) {
  const { t } = useTranslation();
  const { api } = useRepository();
  
  const isAuthenticated = useIsAuthenticated();

  const {data: account} = useQuery({
    queryKey: QueryKeys.account.details(),
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated,
  });

  // Use infinite query for paginated conversations
  const {
    data: conversationsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: QueryKeys.chat.conversations(account?.id),
    queryFn: async ({ pageParam = 1 }) => {
      return api.findAllAccountChat({
        accountId: account?.id!,
        page: pageParam,
        limit: 10,
        sort: JSON.stringify(["createdAt", "DESC"]),
      });
    },
    enabled: isOpen && !!account?.id,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !Array.isArray(lastPage) || lastPage.length < 10) return undefined;
      return (allPages?.length ?? 0) + 1;
    },
    initialPageParam: 1,
  });

  // Flatten infinite query results
  const conversations = conversationsData?.pages?.flat() || [];

  // Group conversations by date
  const groupedConversations = groupItemsByDate(
    conversations || [],
    (conversation) => conversation.createdAt
  );

  const handleSelectThread = (threadId: string) => {
    onSelectThread(threadId);
    onClose();
  };

  const renderConversationSection = (title: string, items: any[]) => {
    if (items.length === 0) return null;
    
    return (
      <div className="mb-4">
        <h3 className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">{title}</h3>
        <div className="space-y-1">
          {items.map((conversation, index) => (
            <motion.div
              key={conversation.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Button
                variant="ghost"
                className="w-full justify-start text-left h-auto p-3 hover:bg-gray-50 transition-colors rounded-xl"
                onClick={() => handleSelectThread(conversation.threadId)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-primary-600" />
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <span className="font-medium text-sm text-gray-900 truncate">
                      {conversation.title || 'Untitled Chat'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatRelativeTime(conversation.createdAt)}
                    </span>
                  </div>
                </div>
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              <span>{t('common.chat.history.title')}</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60dvh] overflow-y-auto p-1">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500 text-sm">
              {t('common.chat.history.loadError')}
            </div>
          ) : conversations && conversations.length > 0 ? (
            <div>
              {renderConversationSection(t('common.chat.history.today'), groupedConversations.today)}
              {renderConversationSection(t('common.chat.history.thisWeek'), groupedConversations.thisWeek)}
              {renderConversationSection(t('common.chat.history.thisMonth'), groupedConversations.thisMonth)}
              {renderConversationSection(t('common.chat.history.older'), groupedConversations.older)}
              
              {/* Load More Button */}
              {hasNextPage && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="w-full justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {isFetchingNextPage ? t('common.loading') : t('common.loadMore')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">{t('common.chat.history.empty')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 